import * as yup from 'yup';
import {isPossiblePhoneNumber} from 'libphonenumber-js';
import { EmployerType } from '~graphqlResources';



// eslint-disable-next-line no-useless-escape
export const VALID_SYMBOLS_REGEX = /[!"#$%&'()*+,\-.\/:;<=>?@\[\\\]\^_`{|}~]/;

export const fullName =
    yup
        .string()
        .required('Enter your first and last name')
        .min(3, ({min}) => `The minimum length of full name should be ${min} or more symbols`)
        .max(100, ({max}) => `The length of full name should be ${max} or fewer symbols`);

export const email =
    yup
        .string()
        .email('Enter valid email').required('Email is required');

export const password =
    yup
        .string()
        .required(('Password is required'))
        .min(3, ({min}) => `Password must be at least ${min} characters long`)
        .max(32, ({max}) => `Password must be no more than ${max} characters`)
        .matches(/[a-zA-Z]+/, 'Password should contain at least one letter')
        .matches(/[\d]+/, 'Password should contain at least one digit')
        .matches(VALID_SYMBOLS_REGEX, 'Password should contain at least one special symbol (e.g. @#$%)')
        .matches(/^[^\s]+$/, 'Spaces are not allowed');

export const confirmPassword =
    yup
        .string()
        .required('Confirm password is required')
        .oneOf([yup.ref('password'), null], 'Passwords don\'t match');

export const readPrivacy =
    yup
        .boolean()
        .oneOf([true], 'Please read and agree with our terms and conditions');

export const selectSingleOptional = yup.object().nullable();

export const selectSingle = selectSingleOptional.required().test({
    name: 'select option is required',
    // eslint-disable-next-line no-template-curly-in-string
    message: '${path} is required to select from the list',
    test: (value) => {
        return !!value?.value;
    }
});

export const selectDirtySingle = selectSingleOptional.required().test({
    name: 'select option is required',
    // eslint-disable-next-line no-template-curly-in-string
    message: '${path} is required to select',
    test: (value) => {
        return !!value?.label;
    }
});

export const selectMultiOptional = yup.array().of(selectSingle);

export const selectMulti = selectMultiOptional
    .required('Select some option')
    .min(1, ({min}) => `At least ${min} option should be selected`);

const userRegisterBaseSchema = yup.object({
    fullName,
    email,
    password,
    confirmPassword,
    readPrivacy
});

export const talentRegisterSchema = userRegisterBaseSchema;
export const enterpriseRegisterSchema = userRegisterBaseSchema.shape({
    enterpriseAccountType: yup.object().required('Choose account type')
});



export const loginSchema = yup.object().shape({
    email: yup.string().required('Please enter your email'),
    password: yup.string().required('Please enter your password')
});



export const forgotPasswordSchema = yup.object().shape({
    email: yup.string().required('Please enter your email')
});



export const resetPasswordSchema = yup.object().shape({
    password,
    confirmPassword
});

export const integerNumberSchema = yup
    .number()
    // Transformed to prevent casting empty string to NaN
    .transform((v, o) => (o === '' ? undefined : v))
    .integer()
    .required();

export const oneDecimalNumberSchema = yup
    .number()
    .transform((v, o) => (o === '' ? undefined : v))
    .positive()
    .test(
        'maxDigitsAfterDecimal',
        'number field must have 1 digits after decimal or less',
        (number) => Number.isFinite(number) ? /^\d+(\.\d?)?$/.test(String(number)) : true
    );

export const yearsOfExperienceSchema = oneDecimalNumberSchema.max(60).required();

export const monthYearSchema = yup.object().shape({
    month: integerNumberSchema
        .min(1)
        .max(12)
        .label('Month'),
    year: integerNumberSchema
        .min(1900)
        .max(2100)
        .label('Year')
});

export const monthYearSelectSchema = yup.object().shape({
    month: selectSingle.label('Month'),
    year: selectSingle.label('Year')
});

export const monthYearSelectSchemaNotRequired = yup.object().shape({
    month: selectSingleOptional.label('Month'),
    year: selectSingleOptional.label('Year')
}).test(
    'monthYearSelectSchemaNotRequired',
    'Month and Year are required',
    function(value) {
        const { month, year } = value;
        // Check if month or year has been touched
        if (month !== undefined || year !== undefined) {
            // If either month or year has been touched, the field is required
            return month !== undefined && year !== undefined;
        }
        // If neither month nor year has been touched, the field is not required
        return true;
    }
);

export const monthYearSelectNullableSchema = yup.object().shape({
    month: selectSingle.label('Month').nullable(),
    year: selectSingle.label('Year').nullable()
});


export const telephoneSchema =
    yup
        .string()
        .test('is-phone-exists', 'Telephone number is required', (value) => !!value)
        .test('is-valid-phone', 'Telephone number is not valid', (value) => value && isPossiblePhoneNumber(value));

export const telephoneSchemaNotRequired =
    yup
        .string()
        .test('is-valid-phone', 'Telephone number is not valid', (value) => {
            if (!value) return true;
            return isPossiblePhoneNumber(value);
        })
        .nullable()
        .notRequired();

export const disableEmployerRequiredSubfield = (subfieldKey: keyof EmployerType) => (
    employer: EmployerType,
    schema: yup.AnySchema
) => {
    return employer?.[subfieldKey] ? schema.notRequired() : schema;
};

export const WEBSITE_REGEXP = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;

export const website = yup.string()
    .trim()
    .matches(
        WEBSITE_REGEXP,
        {
            message: 'Please enter a valid URL',
            excludeEmptyString: true
        }
    )
    .nullable()
    .notRequired();

export const employerCompanyCollection = {
    employer: selectSingle.label('Employer').nullable(),
    website: website,
    industry: selectSingle.label('Industry').nullable().when('employer', disableEmployerRequiredSubfield('industry')),
    size: selectSingle.label('Employer size').nullable().when('employer', disableEmployerRequiredSubfield('size'))
};

export const employerCompanyDirtyCollection = {
    employer: selectDirtySingle.label('Employer').nullable(),
    website: website,
    industry: selectDirtySingle.label('Industry').nullable().when('employer', disableEmployerRequiredSubfield('industry')),
    size: selectDirtySingle.label('Employer size').nullable().when('employer', disableEmployerRequiredSubfield('size'))
};

export const jobTitle = yup
    .string()
    .required('Please enter the title of the job')
    .trim('The job title cannot include leading and trailing spaces')
    .min(3, ({min}) => `The minimum length of the job title should be ${min} or more symbols`)
    .max(100, ({max}) => `The length of the job title should be ${max} or fewer symbols`);

export const jobDescription = yup
    .string()
    .required('Please enter the description of the job')
    .test('is-min', 'The minimum length of the job description should be 3 or more symbols', (value) => {
        const parsedDescription = new DOMParser().parseFromString(value, 'text/html').documentElement.textContent.trim();
        return parsedDescription.length >= 3;
    })
    .test('is-max', 'The length of the job description should be 4500 or fewer symbols', (value) => {
        const parsedDescription = new DOMParser().parseFromString(value, 'text/html').documentElement.textContent.trim();
        return parsedDescription.length <= 4500;
    })
    .required('Please enter the description of the job');
